import React, { useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Flex,
  Skeleton,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useBreakpointValue
} from '@chakra-ui/react';
import {
  MoreVertical,
  Eye,
  Edit3,
  Trash2,
  Calendar,
  MapPin,
  Clock
} from 'lucide-react';
import moment from 'moment';

// Tokens Datofy V1 por categoría — misma asignación que Calendar.js / EventModal.js
const CATEGORY_TOKENS = {
  clase:      { bg: 'sky.bg',    color: 'sky.ink'    },
  reunion:    { bg: 'lav.bg',    color: 'lav.ink'    },
  evaluacion: { bg: 'butter.bg', color: 'butter.ink' },
  actividad:  { bg: 'mint.bg',   color: 'mint.ink'   },
  importante: { bg: 'rose.bg',   color: 'rose.ink'   },
  otro:       { bg: 'teal.bg',   color: 'teal.ink'   },
};

const STATUS_TOKENS = {
  scheduled: { bg: 'sky.bg',    color: 'sky.ink',    label: 'Programado' },
  completed: { bg: 'ok.bg',     color: 'ok.500',     label: 'Completado' },
  cancelled: { bg: 'danger.bg', color: 'danger.500', label: 'Cancelado'  },
};

// Pill reutilizable estilo Datofy V1
const Pill = ({ bg, color, children, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap="4px"
    px="9px"
    py="3px"
    borderRadius="999px"
    bg={bg}
    color={color}
    fontSize="11.5px"
    fontWeight="700"
    {...rest}
  >
    {children}
  </Box>
);

function EventList({
  events = [],
  loading = false,
  onEventClick,
  onEventEdit,
  onEventDelete,
  showActions = true,
  groupByDate = true,
  limit = null
}) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Agrupar eventos por fecha si está habilitado
  const groupedEvents = useMemo(() => {
    if (!groupByDate) {
      return { all: limit ? events.slice(0, limit) : events };
    }

    const groups = {};
    const eventsToProcess = limit ? events.slice(0, limit) : events;

    eventsToProcess.forEach(event => {
      const dateKey = moment(event.start_date).format('YYYY-MM-DD');
      const dateLabel = moment(event.start_date).format('dddd, DD MMMM YYYY');

      if (!groups[dateKey]) {
        groups[dateKey] = { label: dateLabel, events: [] };
      }
      groups[dateKey].events.push(event);
    });

    const sortedGroups = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key];
    });

    return sortedGroups;
  }, [events, groupByDate, limit]);

  const formatEventTime = (event) => {
    if (event.all_day) return 'Todo el día';

    const start = moment(event.start_date);
    const end = moment(event.end_date);

    if (start.isSame(end, 'day')) {
      return `${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    }
    return `${start.format('DD/MM HH:mm')} - ${end.format('DD/MM HH:mm')}`;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'clase': return <Calendar size={13} />;
      case 'reunion': return <Eye size={13} />;
      case 'evaluacion': return <Edit3 size={13} />;
      default: return <Calendar size={13} />;
    }
  };

  const EventItem = ({ event, showDate = false }) => {
    const categoryTokens = CATEGORY_TOKENS[event.category] || CATEGORY_TOKENS.otro;
    const statusTokens = STATUS_TOKENS[event.status] || STATUS_TOKENS.scheduled;

    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor="cream.line"
        borderRadius="14px"
        boxShadow="0 1px 2px rgba(54,38,22,.05)"
        p={4}
        cursor="pointer"
        transition="box-shadow 0.15s, border-color 0.15s"
        _hover={{ boxShadow: '0 4px 12px rgba(54,38,22,.1)', borderColor: 'coral.soft' }}
        onClick={() => onEventClick && onEventClick(event)}
        fontFamily="jakarta"
      >
        <Flex justify="space-between" align="flex-start" gap={3}>
          <VStack align="flex-start" spacing={2} flex={1} minW={0}>
            <HStack spacing={2} wrap="wrap">
              <Text fontWeight="700" color="ink.900" fontSize={isMobile ? '13.5px' : '14.5px'} noOfLines={2} flex={1}>
                {event.title}
              </Text>
              {event.category && (
                <Pill bg={categoryTokens.bg} color={categoryTokens.color}>
                  {getCategoryIcon(event.category)}
                  {!isMobile && event.category}
                </Pill>
              )}
            </HStack>

            {event.description && (
              <Text color="ink.400" fontSize="13px" noOfLines={2}>
                {event.description}
              </Text>
            )}

            <VStack align="flex-start" spacing={1}>
              <HStack spacing={2} fontSize="12.5px" color="ink.400">
                <Clock size={13} />
                <Text>
                  {showDate && `${moment(event.start_date).format('DD/MM/YYYY')} `}{formatEventTime(event)}
                </Text>
              </HStack>

              {event.location && (
                <HStack spacing={2} fontSize="12.5px" color="ink.400">
                  <MapPin size={13} />
                  <Text noOfLines={1}>{event.location}</Text>
                </HStack>
              )}
            </VStack>

            <HStack spacing={2}>
              <Pill bg={statusTokens.bg} color={statusTokens.color}>
                {statusTokens.label || event.status}
              </Pill>
              {event.repeat && event.repeat !== 'none' && (
                <Pill bg="cream.bg2" color="ink.400">Repetir</Pill>
              )}
            </HStack>
          </VStack>

          {showActions && (
            <Menu placement="bottom-end" isLazy>
              <MenuButton
                as={IconButton}
                icon={<MoreVertical size={15} />}
                variant="ghost"
                size="sm"
                color="ink.400"
                borderRadius="9px"
                _hover={{ bg: 'cream.bg2', color: 'coral.500' }}
                onClick={(e) => e.stopPropagation()}
              />
              <Portal appendToParentPortal={false}>
                <MenuList zIndex="popover" fontSize="13px" fontFamily="jakarta">
                  <MenuItem
                    icon={<Eye size={15} />}
                    onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(event); }}
                  >
                    Ver detalles
                  </MenuItem>
                  <MenuItem
                    icon={<Edit3 size={15} />}
                    onClick={(e) => { e.stopPropagation(); onEventEdit && onEventEdit(event); }}
                  >
                    Editar
                  </MenuItem>
                  <MenuItem
                    icon={<Trash2 size={15} />}
                    color="danger.500"
                    _hover={{ bg: 'danger.bg' }}
                    onClick={(e) => { e.stopPropagation(); onEventDelete && onEventDelete(event.id); }}
                  >
                    Eliminar
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          )}
        </Flex>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box p={4}>
        <VStack spacing={3}>
          {[...Array(5)].map((_, index) => (
            <Box key={index} w="100%" bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px" p={4}>
              <VStack align="flex-start" spacing={3}>
                <Skeleton height="16px" width="70%" borderRadius="6px" />
                <Skeleton height="13px" width="90%" borderRadius="6px" />
                <HStack spacing={2}>
                  <Skeleton height="11px" width="60px" borderRadius="6px" />
                  <Skeleton height="11px" width="80px" borderRadius="6px" />
                </HStack>
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Center py={10}>
        <VStack
          spacing={3} py={10} px={6} maxW="380px"
          bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2"
        >
          <Calendar size={40} color="var(--chakra-colors-ink-400)" />
          <VStack spacing={1} textAlign="center">
            <Text fontSize="14.5px" fontWeight="800" color="ink.900">
              No hay eventos
            </Text>
            <Text fontSize="13px" color="ink.400">
              {groupByDate ? 'No se encontraron eventos para mostrar.' : 'Aún no has creado ningún evento.'}
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box p={4}>
      {groupByDate ? (
        <VStack spacing={5} align="stretch">
          {Object.entries(groupedEvents).map(([dateKey, group]) => (
            <Box key={dateKey}>
              <Text
                fontSize="13px" fontWeight="800" color="ink.400" mb={3}
                textTransform="uppercase" letterSpacing="0.04em"
              >
                {group.label}
              </Text>
              <VStack spacing={3} align="stretch">
                {group.events.map(event => (
                  <EventItem key={event.id} event={event} showDate={false} />
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {(limit ? events.slice(0, limit) : events).map(event => (
            <EventItem key={event.id} event={event} showDate={true} />
          ))}
        </VStack>
      )}
    </Box>
  );
}

export default EventList;
